import type { MemberOut, TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, EmptyState, ErrorState, Icon, TableSkeletonRows, toast } from "@sagot/ui";
import type { InvitationOut } from "../features/workspace/shared/api-types";
import { useErrorMessage } from "../lib/api-errors";
import { useInviteActions, usePendingInvites } from "./use-team";

interface PendingInvitesProps {
  columns: number;
  members: readonly MemberOut[];
  teams: readonly TeamOut[];
  canInvite: boolean;
  format: { date: (iso: string | null) => string; relative: (iso: string | null) => string };
}

function InviteMenu({ invite }: { invite: InvitationOut }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { resend, revoke } = useInviteActions();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" iconOnly aria-label={t("team.inviteActions", { email: invite.email })}>
          <Icon name="more-horizontal" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          onSelect={() => {
            resend.mutate(invite.id, { onSuccess: () => toast.success(t("team.inviteResent", { email: invite.email })), onError: (e) => toast.error(message(e)) });
          }}
        >
          <Icon name="send" />
          {t("team.resendInvite")}
        </DropdownMenuItem>
        <DropdownMenuItem
          tone="danger"
          onSelect={() => {
            revoke.mutate(invite.id, { onSuccess: () => toast.success(t("team.inviteRevoked", { email: invite.email })), onError: (e) => toast.error(message(e)) });
          }}
        >
          <Icon name="x-circle" />
          {t("team.revokeInvite")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Pending invitations as table rows (Members card, "Pending" tab): resend / revoke. */
export function PendingInviteRows({ columns, members, teams, canInvite, format }: PendingInvitesProps) {
  const { t } = useTranslation();
  const invites = usePendingInvites();
  if (invites.isPending) return <TableSkeletonRows rows={2} columns={columns} />;
  if (invites.isError) {
    return (
      <tr>
        <td colSpan={columns}>
          <ErrorState headingLevel={3} title={t("team.loadError")} onRetry={() => void invites.refetch()} retryLabel={t("common.retry")} />
        </td>
      </tr>
    );
  }
  if (invites.data.length === 0) {
    return (
      <tr>
        <td colSpan={columns}>
          <EmptyState icon="mail" headingLevel={3} title={t("team.emptyPendingTitle")} description={t("team.emptyPendingBody")} />
        </td>
      </tr>
    );
  }
  return invites.data.map((invite) => {
    const inviter = members.find((m) => m.user_id === invite.invited_by || m.id === invite.invited_by);
    const team = teams.find((x) => x.id === invite.team_id);
    const line = inviter
      ? t("team.invitedBy", { name: inviter.name, date: format.date(invite.created_at), expires: format.relative(invite.expires_at) })
      : t("team.invitedOn", { date: format.date(invite.created_at), expires: format.relative(invite.expires_at) });
    return (
      <tr key={invite.id}>
        <td>
          <div className="flex items-center gap-2.5">
            <span className="avatar bg-sunken text-fg-subtle">
              <Icon name="mail" className="size-4" />
            </span>
            <div className="min-w-0">
              <div className="truncate font-semibold">{invite.email}</div>
              <div className="text-xs text-fg-subtle">{line}</div>
            </div>
          </div>
        </td>
        <td className="text-xs font-semibold text-fg-muted">{invite.role_keys.map((key) => t(`console.roles.${key.split(":")[0] ?? key}`, { defaultValue: key })).join(", ")}</td>
        <td>
          <Badge>{team ? team.name : t("team.noTeams")}</Badge>
        </td>
        <td>
          <Badge>
            <Icon name="clock" className="size-3" />
            {t("team.pendingInvite")}
          </Badge>
        </td>
        <td className="text-fg-subtle">{t("team.never")}</td>
        <td className="text-right">{canInvite ? <InviteMenu invite={invite} /> : null}</td>
      </tr>
    );
  });
}
